import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { SupplierRequestFullType } from '@econtractor/types';
import * as apiSupplierRequests from 'api/apiSupplierRequests';
import { useToast } from 'context/ToastContext';
import { safeFloatParse, safeIntParse } from 'utils/helpers/safeParsing';
import { toNullableObjectId } from './itemLine';
import { SUPPLIER_REQUEST_STATUS } from './constants';
import type { SupplierRequestDraftItem } from './types';

interface UseViewSupplierRequestParams {
    requestId: number | null;
    onClose: () => void;
}

const useViewSupplierRequest = ({ requestId, onClose }: UseViewSupplierRequestParams) => {
    const { t } = useTranslation(['Vendors', 'common']);
    const { showToast } = useToast();
    const [request, setRequest] = useState<SupplierRequestFullType | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        const id = safeIntParse(requestId);
        if (!id) {
            setRequest(null);
            setIsLoading(false);
            return;
        }

        let cancelled = false;
        setRequest(null);
        setIsLoading(true);

        void (async () => {
            const res = await apiSupplierRequests.getById(id);
            if (cancelled) return;
            setIsLoading(false);
            if (!res) {
                showToast(t('common:messError'), 'danger');
                onClose();
                return;
            }
            setRequest(res);
        })();

        return () => {
            cancelled = true;
        };
        // Only when requestId changes — not when toast/i18n identities change after mutations
    }, [requestId]);

    const items: SupplierRequestDraftItem[] = useMemo(
        () =>
            (request?.items || []).map((item) => ({
                ID: safeIntParse(item.ID) || 0,
                productId: safeIntParse(item.productId) || 0,
                prodName: String(item.prodName || ''),
                prodArticle: String(item.prodArticle || ''),
                itemCount: safeIntParse(item.itemCount) || 0,
                receivedCount: safeIntParse(item.receivedCount) || 0,
                objectId: toNullableObjectId(item.objectId),
                objectName: String(item.objectName || ''),
                clientName: String(item.clientName || ''),
                onlySale: Number(item.onlySale || 0),
                objectStatus:
                    item.objectStatus == null ? null : Number(item.objectStatus),
                dimensionName: String(item.dimensionName || ''),
                dimensionI18nKey: String(item.dimensionI18nKey || ''),
                prodDefPrice: safeFloatParse(item.prodDefPrice) || 0,
                prodDefPriceSale: safeFloatParse(item.prodDefPriceSale) || 0,
                vendorName: String(item.vendorName || request?.vendorName || ''),
            })),
        [request],
    );

    const hasRemaining = useMemo(
        () =>
            items.some(
                (item) =>
                    (safeIntParse(item.itemCount) || 0) -
                        (safeIntParse(item.receivedCount) || 0) >
                    0,
            ),
        [items],
    );

    const isActive = Number(request?.status) === SUPPLIER_REQUEST_STATUS.ACTIVE;

    return {
        t,
        request,
        setRequest,
        items,
        isLoading,
        isActive,
        hasRemaining,
        handleHide: onClose,
    };
};

export default useViewSupplierRequest;
